/*
 * <utsav-slides data-autoplay="5000"> — cross-fading background slides for the Utsav hero.
 * Children: .utsav-hero__slide (one has .is-active) and optional .utsav-hero__dot buttons (data-slide).
 * Autoplay pauses for keyboard focus, hidden tabs and prefers-reduced-motion. Supports swipe.
 * It does not pause on hover/tap: phones emulate "mouseenter" on tap without a matching "mouseleave",
 * which used to stop the slideshow for good after one touch.
 * Slides change by fading the new one in on top of the old one, so the picture never dips to the
 * background colour mid-transition.
 */
(() => {
  if (customElements.get('utsav-slides')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class UtsavSlides extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.utsav-hero__slide')];
      this.dots = [...this.querySelectorAll('.utsav-hero__dot')];
      if (this.slides.length < 2) return;

      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.delay = Number(this.dataset.autoplay) || 0;
      this.paused = false;

      this.dots.forEach((dot) => dot.addEventListener('click', () => this.show(Number(dot.dataset.slide), true)));

      // Pause only while a keyboard user has focus inside the hero (a tap or click focuses too, so
      // only :focus-visible counts).
      const section = this.closest('.shopify-section') || this;
      const isKeyboardFocus = (element) => {
        try {
          return element.matches(':focus-visible');
        } catch (error) {
          return false; // Older Safari does not know :focus-visible; never pause there.
        }
      };
      this.onFocusIn = (event) => { if (isKeyboardFocus(event.target)) this.paused = true; };
      this.onFocusOut = (event) => { if (!section.contains(event.relatedTarget)) this.paused = false; };
      section.addEventListener('focusin', this.onFocusIn);
      section.addEventListener('focusout', this.onFocusOut);
      this.section = section;

      // Load the other slides' images up front so a slide never fades in before its photo is ready.
      const loadAll = () => this.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      if (document.readyState === 'complete') loadAll();
      else window.addEventListener('load', loadAll, { once: true });

      // Horizontal swipe on touch screens.
      this.onPointerDown = (event) => { this.startX = event.clientX; this.startY = event.clientY; };
      this.onPointerUp = (event) => {
        if (this.startX == null) return;
        const dx = event.clientX - this.startX;
        const dy = event.clientY - this.startY;
        this.startX = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.show(this.index + (dx < 0 ? 1 : -1), true);
      };
      // A vertical scroll cancels the pointer; forget the start point so it is not reused.
      this.onPointerCancel = () => { this.startX = null; };
      section.addEventListener('pointerdown', this.onPointerDown);
      section.addEventListener('pointerup', this.onPointerUp);
      section.addEventListener('pointercancel', this.onPointerCancel);

      if (this.delay) this.timer = window.setInterval(() => this.tick(), this.delay);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      window.clearTimeout(this.leaveTimer);
      if (!this.section) return;
      this.section.removeEventListener('focusin', this.onFocusIn);
      this.section.removeEventListener('focusout', this.onFocusOut);
      this.section.removeEventListener('pointerdown', this.onPointerDown);
      this.section.removeEventListener('pointerup', this.onPointerUp);
      this.section.removeEventListener('pointercancel', this.onPointerCancel);
    }

    tick() {
      if (this.paused || document.hidden || reducedMotion.matches) return;
      this.show(this.index + 1);
    }

    show(target, fromUser = false) {
      const count = this.slides.length;
      const next = ((target % count) + count) % count;
      if (next === this.index) return;

      // The old slide stays fully visible underneath (.is-leaving) while the new one fades in on top.
      const previous = this.slides[this.index];
      this.slides.forEach((slide) => slide.classList.remove('is-leaving'));
      previous.classList.remove('is-active');
      previous.classList.add('is-leaving');
      previous.setAttribute('aria-hidden', 'true');
      this.slides[next].classList.add('is-active');
      this.slides[next].removeAttribute('aria-hidden');
      window.clearTimeout(this.leaveTimer);
      this.leaveTimer = window.setTimeout(() => previous.classList.remove('is-leaving'), 1000);

      this.dots.forEach((dot, i) => {
        if (i === next) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      this.index = next;

      // Restart the timer after a manual change so the chosen slide gets its full time.
      if (fromUser && this.delay) {
        window.clearInterval(this.timer);
        this.timer = window.setInterval(() => this.tick(), this.delay);
      }
    }
  }

  customElements.define('utsav-slides', UtsavSlides);
})();
